/**
 * return-to — pure URL helpers for post-auth navigation.
 *
 * All functions are stateless and window-safe: they return `null` / `undefined`
 * when called server-side. Used by `AuthSession` and `SocialFlowCoordinator`.
 *
 * The "return-to" pattern lets us send the user back to the page they were on
 * before a sign-in redirect. The URL is encoded into the OAuth `state` parameter
 * so it survives the IDP round-trip.
 */

/** Returns the current page as a sanitized relative URL, or `null` on the server. */
export function currentRelativeUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const candidate = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return sanitizeReturnTo(candidate);
}

/** Returns the current page as a full absolute URL, or `undefined` on the server. */
export function currentAbsoluteUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.location.href;
}

/**
 * Percent-encodes a `returnTo` path for embedding in the OAuth `state` parameter.
 * Returns `undefined` when `returnTo` is falsy so callers can pass it directly
 * as an optional argument without extra null-checks.
 */
export function encodeReturnTo(returnTo: string | null): string | undefined {
  if (!returnTo) return undefined;
  return encodeURIComponent(returnTo);
}

/**
 * Decodes a `returnTo` value from the OAuth `state` parameter and sanitizes it.
 * Returns `null` if decoding fails or the result is not a safe same-origin path.
 */
export function decodeReturnTo(value: string): string | null {
  try {
    return sanitizeReturnTo(decodeURIComponent(value));
  } catch (err) {
    console.warn('[auth] decodeReturnTo: failed to decode return-to value', err);
    return null;
  }
}

/**
 * Ensures `value` is a safe same-origin relative path.
 *
 * Returns `null` for:
 * - Cross-origin URLs (open-redirect protection)
 * - Protocol-relative paths (`//evil.com`)
 * - Server-side calls (`window` is undefined)
 * - Malformed URLs that throw during parsing
 */
export function sanitizeReturnTo(value: string | null): string | null {
  if (!value || typeof window === 'undefined') return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const relative = `${url.pathname}${url.search}${url.hash}`;
    if (!relative.startsWith('/') || relative.startsWith('//')) return null;
    return relative;
  } catch (err) {
    console.warn('[auth] sanitizeReturnTo: failed to parse URL', err);
    return null;
  }
}

/**
 * Navigates to the appropriate destination after a social auth flow completes.
 *
 * Priority order:
 * 1. `returnTo` is a valid same-origin path → navigate there.
 * 2. Currently on an `/auth/` page with no `returnTo` → redirect to `/`
 *    to avoid leaving the user stuck on the callback page.
 * 3. Otherwise → strip OAuth callback params (`code`, `state`, `error`,
 *    `error_description`) from the current URL in-place via `history.replaceState`.
 *
 * @param returnTo - The path to return to, or `null` to fall back to the rules above.
 * @param replaceLocation - Injected navigation function (wraps `window.location.assign` or a custom handler).
 */
export function navigateAfterAuth(
  returnTo: string | null,
  replaceLocation: (url: string) => void,
): void {
  if (typeof window === 'undefined') return;
  const target = sanitizeReturnTo(returnTo);
  if (target) {
    replaceLocation(target);
  } else if (window.location.pathname.startsWith('/auth/')) {
    replaceLocation('/');
  } else {
    stripCallbackParams();
  }
}

/**
 * Removes OAuth callback query params from the current URL without a page reload.
 * Called when the user lands back on a non-auth page after a social redirect.
 */
function stripCallbackParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  window.history.replaceState(null, '', `${url.pathname}${url.search || ''}${url.hash}`);
}
